import type { Building, BuildingStore } from '../buildings/BuildingStore';
import { statsAt, ECONOMY, POPULATION, buildingDef } from '../buildings/definitions';
import { isPlayerId, type PlayerId } from '../ids';

export type LeaveReason = 'starvation' | 'homeless' | 'recruited';

export type ColonyEvent =
  /** Population, capacity or worker numbers changed for a player (at most once per step / command). */
  | { type: 'changed'; playerId: PlayerId }
  | { type: 'joined'; playerId: PlayerId; count: number }
  | { type: 'left'; playerId: PlayerId; count: number; reason: LeaveReason };

export interface ColonySettings {
  readonly startingSurvivors: number;
  readonly maxPopulation: number;
  readonly growthIntervalSeconds: number;
  readonly growthMinFood: number;
}

export interface ColonySummary {
  readonly population: number;
  /** min(housing from complete buildings, maxPopulation). */
  readonly capacity: number;
  readonly assigned: number;
  readonly idle: number;
  /** Food eaten per upkeep cycle. */
  readonly foodNeeded: number;
}

export interface ColonySnapshot {
  readonly players: Record<string, { population: number; growthElapsed: number }>;
  /** Workplaces: building id → { workers, target }. */
  readonly workplaces: Record<string, { workers: number; target: number }>;
}

export type SetWorkersCheck = { ok: true } | { ok: false; code: 'not_a_workplace' | 'invalid_payload' | 'not_enough_survivors'; message: string };

const EPS = 1e-9;

/**
 * Survivors and their jobs, per player (DECISIONS D51–D53). Engine-free and
 * deterministic; advanced by the simulation's fixed ticks.
 *
 *  - Population never exceeds capacity (housing of COMPLETE buildings, capped
 *    by maxPopulation). If capacity drops, the excess leave at once (homeless).
 *  - A newcomer joins after `growthIntervalSeconds` of good conditions:
 *    room in the housing, at least `growthMinFood` food, no shortage.
 *  - Each workplace has `workerSlots`; the player sets a target (default: all
 *    slots). Idle survivors fill targets, lowest building id first; when
 *    survivors are lost, workers are released from the highest id first.
 *  - Survivors eat ECONOMY.foodPerSurvivor each upkeep cycle (the Economy asks
 *    `foodNeeded`); each cycle that cannot be paid in full, one survivor leaves.
 */
export class Colony {
  private readonly pop = new Map<PlayerId, number>();
  private readonly growth = new Map<PlayerId, number>();
  private readonly workers = new Map<number, number>();
  private readonly targets = new Map<number, number>();
  private readonly listeners = new Set<(e: ColonyEvent) => void>();
  private readonly dirty = new Set<PlayerId>();
  private readonly unsubscribe: () => void;

  constructor(
    private readonly store: BuildingStore,
    players: readonly PlayerId[],
    private readonly cfg: ColonySettings = POPULATION,
  ) {
    for (const p of players) {
      this.growth.set(p, 0);
      this.pop.set(p, 0);
    }
    for (const b of store.all()) this.onComplete(b);
    for (const p of players) this.pop.set(p, Math.min(Math.max(0, Math.floor(cfg.startingSurvivors)), this.capacity(p)));
    for (const p of players) this.rebalance(p);
    this.dirty.clear();
    this.unsubscribe = store.subscribe((e) => {
      if (e.type === 'completed' || (e.type === 'added' && e.building.state === 'complete')) this.onComplete(e.building);
      else if (e.type === 'removed') this.onRemoved(e.building);
      else if (e.type === 'upgraded' && isPlayerId(e.building.ownerId) && this.pop.has(e.building.ownerId)) this.dirty.add(e.building.ownerId);
    });
  }

  get listenerCount(): number {
    return this.listeners.size;
  }

  subscribe(l: (e: ColonyEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  dispose(): void {
    this.unsubscribe();
    this.listeners.clear();
  }

  population(p: PlayerId): number {
    return this.pop.get(p) ?? 0;
  }

  /** Housing from complete buildings, capped by the population limit. */
  capacity(p: PlayerId): number {
    let n = 0;
    for (const b of this.store.ownedBy(p)) if (b.state === 'complete') n += statsAt(b.type, b.level).housing;
    return Math.min(n, this.cfg.maxPopulation);
  }

  assigned(p: PlayerId): number {
    let n = 0;
    for (const b of this.store.ownedBy(p)) n += this.workers.get(b.id) ?? 0;
    return n;
  }

  idle(p: PlayerId): number {
    return this.population(p) - this.assigned(p);
  }

  foodNeeded(p: PlayerId): number {
    return this.population(p) * ECONOMY.foodPerSurvivor;
  }

  /** Workers at a building (0 for non-workplaces). */
  workersAt(id: number): number {
    return this.workers.get(id) ?? 0;
  }

  /** Target workers at a building (what the player asked for; defaults to all slots). */
  targetAt(id: number): number {
    return this.targets.get(id) ?? 0;
  }

  slotsAt(id: number): number {
    const b = this.store.get(id);
    return b ? buildingDef(b.type).workerSlots ?? 0 : 0;
  }

  /** Share of full speed a workplace runs at (workers / slots); 0 when unstaffed or not complete. */
  workRatio(id: number): number {
    const slots = this.slotsAt(id);
    return slots > 0 ? this.workersAt(id) / slots : 0;
  }

  summary(p: PlayerId): ColonySummary {
    const population = this.population(p);
    const capacity = this.capacity(p);
    const assigned = this.assigned(p);
    return {
      population,
      capacity,
      assigned,
      idle: population - assigned,
      foodNeeded: this.foodNeeded(p),
    };
  }

  /** Seconds until the next newcomer if conditions stay good (null if growth is blocked by `canGrow`). */
  nextArrival(p: PlayerId, canGrow: boolean): number | null {
    if (!canGrow || this.population(p) >= this.capacity(p)) return null;
    return Math.max(0, this.cfg.growthIntervalSeconds - (this.growth.get(p) ?? 0));
  }

  /** Validate a worker change. */
  checkSetWorkers(p: PlayerId, id: number, count: number): SetWorkersCheck {
    const b = this.store.get(id);
    if (!b || b.ownerId !== p) return { ok: false, code: 'not_a_workplace', message: 'That is not one of your workplaces.' };
    const slots = buildingDef(b.type).workerSlots ?? 0;
    if (slots === 0) return { ok: false, code: 'not_a_workplace', message: `The ${buildingDef(b.type).name} has no jobs.` };
    if (!Number.isInteger(count) || count < 0 || count > slots) return { ok: false, code: 'invalid_payload', message: `Workers must be 0–${slots}.` };
    // Only staffed once complete; until then the number is a target.
    if (b.state === 'complete') {
      const extra = count - this.workersAt(id);
      if (extra > this.idle(p)) return { ok: false, code: 'not_enough_survivors', message: 'No idle survivors — build housing so more survivors can join.' };
    }
    return { ok: true };
  }

  /** Apply a validated worker change: it becomes the building's target. */
  setWorkers(p: PlayerId, id: number, count: number): void {
    this.targets.set(id, count);
    const b = this.store.get(id);
    if (b?.state === 'complete') this.workers.set(id, count);
    this.rebalance(p);
    this.dirty.add(p);
  }

  /** Recruitment foundation (Phase 5): can `n` survivors be taken from the colony? */
  canRecruit(p: PlayerId, n: number): boolean {
    return Number.isInteger(n) && n > 0 && n <= this.population(p);
  }

  /** Take `n` survivors out of the colony (idle first, then workers from the highest building id). */
  recruit(p: PlayerId, n: number): void {
    if (!this.canRecruit(p, n)) throw new Error('Cannot recruit');
    this.remove(p, n, 'recruited');
  }

  /** Survivors come back (e.g. a cancelled recruit), up to free housing. Returns how many were taken in. */
  returnSurvivors(p: PlayerId, n: number): number {
    const add = Math.max(0, Math.min(n, this.capacity(p) - this.population(p)));
    if (add > 0) {
      this.pop.set(p, this.population(p) + add);
      this.rebalance(p);
      this.dirty.add(p);
    }
    return add;
  }

  /** One upkeep cycle could not be paid in full: one survivor leaves. */
  starve(p: PlayerId): void {
    if (this.population(p) > 0) this.remove(p, 1, 'starvation');
  }

  /** Advance growth. `canGrow(p)` reports whether food conditions allow newcomers. */
  step(dt: number, canGrow: (p: PlayerId) => boolean): void {
    if (!(dt > 0)) return;
    for (const p of [...this.pop.keys()].sort()) {
      const room = this.population(p) < this.capacity(p);
      if (!room || !canGrow(p)) {
        this.growth.set(p, 0);
        continue;
      }
      let e = (this.growth.get(p) ?? 0) + dt;
      if (e >= this.cfg.growthIntervalSeconds - EPS) {
        e = 0;
        this.pop.set(p, this.population(p) + 1);
        this.rebalance(p);
        this.dirty.add(p);
        this.emit({ type: 'joined', playerId: p, count: 1 });
      }
      this.growth.set(p, e);
    }
    this.flush();
  }

  flush(): void {
    for (const p of [...this.dirty].sort()) this.emit({ type: 'changed', playerId: p });
    this.dirty.clear();
  }

  snapshot(): ColonySnapshot {
    const players: ColonySnapshot['players'] = {};
    for (const p of [...this.pop.keys()].sort()) players[p] = { population: this.population(p), growthElapsed: this.growth.get(p) ?? 0 };
    const workplaces: ColonySnapshot['workplaces'] = {};
    for (const id of [...this.targets.keys()].sort((a, b) => a - b)) workplaces[String(id)] = { workers: this.workersAt(id), target: this.targetAt(id) };
    return { players, workplaces };
  }

  // ------------------------------------------------------------------

  private remove(p: PlayerId, n: number, reason: LeaveReason): void {
    const count = Math.min(n, this.population(p));
    if (count <= 0) return;
    this.pop.set(p, this.population(p) - count);
    this.rebalance(p);
    this.dirty.add(p);
    this.emit({ type: 'left', playerId: p, count, reason });
  }

  /** Release workers if there are more than survivors, then fill targets from the idle pool. */
  private rebalance(p: PlayerId): void {
    const places = this.store
      .ownedBy(p)
      .filter((b) => b.state === 'complete' && (buildingDef(b.type).workerSlots ?? 0) > 0)
      .map((b) => b.id); // ownedBy is id-ordered
    let over = this.assigned(p) - this.population(p);
    for (let i = places.length - 1; i >= 0 && over > 0; i--) {
      const id = places[i]!;
      const take = Math.min(over, this.workersAt(id));
      this.workers.set(id, this.workersAt(id) - take);
      over -= take;
    }
    let idle = this.idle(p);
    for (const id of places) {
      if (idle <= 0) break;
      const want = this.targetAt(id) - this.workersAt(id);
      if (want <= 0) continue;
      const add = Math.min(want, idle);
      this.workers.set(id, this.workersAt(id) + add);
      idle -= add;
    }
  }

  private onComplete(b: Building): void {
    if (!isPlayerId(b.ownerId) || !this.pop.has(b.ownerId)) return;
    const def = buildingDef(b.type);
    if (def.workerSlots) {
      if (!this.targets.has(b.id)) this.targets.set(b.id, def.workerSlots);
      if (!this.workers.has(b.id)) this.workers.set(b.id, 0);
    }
    if (def.workerSlots || def.housing) {
      this.rebalance(b.ownerId);
      this.dirty.add(b.ownerId);
    }
  }

  private onRemoved(b: Building): void {
    this.workers.delete(b.id);
    this.targets.delete(b.id);
    if (!isPlayerId(b.ownerId) || !this.pop.has(b.ownerId)) return;
    const p = b.ownerId;
    const def = buildingDef(b.type);
    if (!def.workerSlots && !def.housing) return;
    const homeless = this.population(p) - this.capacity(p);
    if (homeless > 0) this.remove(p, homeless, 'homeless');
    this.rebalance(p);
    this.dirty.add(p);
  }

  private emit(e: ColonyEvent): void {
    for (const l of this.listeners) l(e);
  }
}
